import React, { useEffect, useState } from 'react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { AlertCircle } from 'lucide-react';
import '../../styles/farming-results-theme.css';


// Import main components
import FarmingResultsLayout from '../../components/farming-results/FarmingResultsLayout';
import FarmingProductHeader from '../../components/farming-results/FarmingProductHeader';
import { FarmingMetricsDashboard } from '../../components/farming-results/FarmingMetricsDashboard';
import StillActiveCstsDashboard from '../../components/farming-results/StillActiveCstsDashboard';
import FarmingHistoryDashboard from '../../components/farming-results/FarmingHistoryDashboard';
import NotesDashboard from '../../components/farming-results/NotesDashboard';
import SeedsToPossiblyAdjustDashboard from '../../components/farming-results/SeedsToPossiblyAdjustDashboard';
import ActiveNegativesDashboard from '../../components/farming-results/ActiveNegativesDashboard';
import StillActiveWordsDashboard from '../../components/farming-results/StillActiveWordsDashboard';

// Import context
import { useFarmingResultsSelection } from '../../context/FarmingResultsSelectionContext';

// Dashboard types
type FarmingDashboardType = 'farming-metrics' | 'still-active-csts' | 'farming-history' | 'notes' | 'seeds-to-possibly-adjust' | 'active-negatives' | 'still-active-words';

// LocalStorage key for dashboard selection
const LS_DASHBOARD_KEY = 'farmingResults_selectedDashboard';

// Dashboard definitions
const farmingDashboards = [
  { value: 'farming-metrics' as FarmingDashboardType, label: 'Farming Metrics' },
  { value: 'still-active-csts' as FarmingDashboardType, label: 'Still Active CSTs' },
  { value: 'farming-history' as FarmingDashboardType, label: 'Farming History' },
  { value: 'seeds-to-possibly-adjust' as FarmingDashboardType, label: 'Seeds to Possibly Adjust' },
  { value: 'active-negatives' as FarmingDashboardType, label: 'Active Negatives' },
  { value: 'still-active-words' as FarmingDashboardType, label: 'Still Active Words' },
  { value: 'notes' as FarmingDashboardType, label: 'Notes' },
];


/**
 * Main Farming Results application page
 * Provides a focused view of farming metrics from the pmt2_data collection
 */
const FarmingResults: React.FC = () => {
  usePageTitle('Farming Results | JLS Apps');
  
  const { isAdmin } = useIsAdmin();
  const { selectedCompany, selectedProduct, loading, error } = useFarmingResultsSelection();
  
  // Initialize dashboard state from localStorage or default
  const [selectedDashboard, setSelectedDashboardInternal] = useState<FarmingDashboardType>(() => {
    const savedDashboard = localStorage.getItem(LS_DASHBOARD_KEY) as FarmingDashboardType | null;
    if (savedDashboard && farmingDashboards.some(d => d.value === savedDashboard)) {
      return savedDashboard;
    }
    return 'farming-metrics'; // Default dashboard
  });

  // Handle dashboard selection and save to localStorage
  const handleDashboardSelect = (dashboard: FarmingDashboardType) => {
    try {
      localStorage.setItem(LS_DASHBOARD_KEY, dashboard || '');
    } catch (e) {
      console.error("Failed to save dashboard to localStorage", e);
    }
    setSelectedDashboardInternal(dashboard);
  };

  useEffect(() => {
    // Any initialization logic can go here
    console.log('Farming Results app initialized');
  }, []);

  // Show loading state
  if (loading) {
    return (
      <FarmingResultsLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-app-primary mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading farming data...</p>
          </div>
        </div>
      </FarmingResultsLayout>
    );
  }

  // Show error state
  if (error) {
    return (
      <FarmingResultsLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center max-w-md">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Error Loading Data
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {error}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-app-primary text-white rounded-md hover:bg-app-primary-dark transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </FarmingResultsLayout>
    );
  }

  // Show selection prompt if no company/product selected
  if (!selectedCompany || !selectedProduct) {
    return (
      <FarmingResultsLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center max-w-md">
            <div className="h-12 w-12 bg-app-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-app-primary text-xl font-semibold">🌱</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Select Company & ASIN
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Choose a company and ASIN from the sidebar to view farming metrics and results.
            </p>
          </div>
        </div>
      </FarmingResultsLayout>
    );
  }

  // Main content with farming metrics dashboard
  return (
    <FarmingResultsLayout>
      <div className="space-y-6">
        {/* Product Header with Image - Same as PMT3 */}
        <FarmingProductHeader product={selectedProduct} />

        {/* Navigation Bar - PMT3 Style Buttons */}
        <div className="bg-gray-800 dark:bg-gray-900 p-4 rounded-lg shadow-sm">
          <div className="flex flex-wrap gap-2 justify-center">
            {farmingDashboards.map((dashboard) => (
              <button
                key={dashboard.value}
                onClick={() => handleDashboardSelect(dashboard.value)}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  selectedDashboard === dashboard.value
                    ? 'bg-[#10b981] text-white shadow-md border-2 border-white'
                    : 'bg-[#059669] text-white hover:bg-[#10b981] hover:shadow-md border-2 border-transparent'
                }`}
              >
                {dashboard.label}
              </button>
            ))}
          </div>
        </div>

        {/* Main Dashboard Content */}
        <div className="max-w-6xl">
          {selectedDashboard === 'farming-metrics' && (
            <FarmingMetricsDashboard product={selectedProduct} />
          )}
          {selectedDashboard === 'still-active-csts' && (
            <StillActiveCstsDashboard />
          )}
          {selectedDashboard === 'farming-history' && (
            <FarmingHistoryDashboard />
          )}
          {selectedDashboard === 'seeds-to-possibly-adjust' && (
            <SeedsToPossiblyAdjustDashboard />
          )}
          {selectedDashboard === 'active-negatives' && (
            <ActiveNegativesDashboard />
          )}
          {selectedDashboard === 'still-active-words' && (
            <StillActiveWordsDashboard />
          )}
          {selectedDashboard === 'notes' && (
            <NotesDashboard />
          )}
        </div>
      </div>
    </FarmingResultsLayout>
  );
};

export default FarmingResults;